<template>
    <div class="print-page-container">
        <!-- 載入指示器 -->
        <div v-if="isLoading" class="loading-container">
            <div class="spinner">Loading...</div>
        </div>

        <!-- 列印內容 -->
        <div v-else class="print-content">
            <div class="print-form-container">
                <!-- 第一頁：申請人資訊 -->
                <div class="print-page page-1">
                    <!-- 表頭 -->
                    <div class="form-header">
                        <div class="header-left">
                            <h1 class="university-name-zh">國立清華大學{{ applicationData.semesterInfo_Print }}外籍生申請表</h1>
                            <h2 class="university-name-en">National Tsing Hua University</h2>
                            <h3 class="form-title">{{ applicationData.semesterInfo_Print_EN }} Online Application Form
                            </h3>
                        </div>
                        <div class="header-right">
                            <div class="photo-box">
                                <img v-if="applicationData.photoPreviewUrl" :src="getPhotoUrl()"
                                    alt="Applicant Photo" />
                                <div v-else class="photo-placeholder">Photo</div>
                            </div>
                        </div>
                    </div>

                    <!-- 申請人資訊區塊 -->
                    <div class="section">
                        <h4 class="section-title">Applicant Information 申請人資料</h4>

                        <table class="info-table">
                            <tr>
                                <td class="label-cell" colspan="1" style="width: 25%;">
                                    <strong>Applicant No.</strong> 申請編號：
                                </td>
                                <td class="value-cell" colspan="3" style="width: 75%;">{{ applicationData.applyID || ''
                                }}</td>
                            </tr>
                            <tr>
                                <td class="label-cell" colspan="4">
                                    <strong>English Name</strong> 英文姓名 (Please print as it appears in your passport)<br>
                                </td>
                            </tr>
                            <tr>
                                <td class="value-cell name-cell" style="width: 25%;">
                                    <span v-if="applicationData.gender === 1">Mr.</span>
                                    <span v-if="applicationData.gender === 2">Miss.</span>
                                </td>
                                <td class="value-cell name-cell" style="width: 25%;">
                                    {{ applicationData.nameFirst || '' }}<br>
                                    <span class="field-label">(first name)</span>
                                </td>
                                <td class="value-cell name-cell" style="width: 25%;">
                                    {{ applicationData.nameMiddle || '' }}<br>
                                    <span class="field-label">(middle name)</span>
                                </td>
                                <td class="value-cell name-cell" style="width: 25%;">
                                    {{ applicationData.nameLast || '' }}<br>
                                    <span class="field-label">(last name)</span>
                                </td>
                            </tr>
                            <tr>
                                <td class="label-cell" colspan="1" style="width: 25%;">
                                    <strong>Chinese Name</strong> 中文姓名
                                </td>
                                <td class="value-cell" colspan="3" style="width: 75%;">
                                    {{ applicationData.chineseName || '' }}
                                </td>
                            </tr>
                            <tr>
                                <td class="label-cell" colspan="2" style="width: 50%;">
                                    <strong>Contact Email</strong> 聯絡電郵
                                </td>
                                <td class="label-cell" colspan="2" style="width: 50%;">
                                    <strong>Date of Birth</strong> 生日 (year-month-date)
                                </td>
                            </tr>
                            <tr>
                                <td class="value-cell" colspan="2" style="width: 50%;">
                                    {{ applicationData.contactEmail || '' }}
                                </td>
                                <td class="value-cell" colspan="2" style="width: 50%;">
                                    {{ applicationData.dateOfBirth || '' }}
                                </td>
                            </tr>
                            <tr>
                                <td class="label-cell" colspan="2" style="width: 50%;">
                                    <strong>Country of Birth</strong> 出生地
                                </td>
                                <td class="label-cell" colspan="2" style="width: 50%;">
                                    <strong>Country of Citizenship</strong> 國籍
                                </td>
                            </tr>
                            <tr>
                                <td class="value-cell" colspan="2" style="width: 50%;">
                                    {{ getCountryName(applicationData.countryOfBirth) }}
                                </td>
                                <td class="value-cell" colspan="2" style="width: 50%;">
                                    {{ getCountryName(applicationData.countryOfCitizenship) }}
                                </td>
                            </tr>

                            <tr>
                                <td class="label-cell" colspan="2" style="width: 50%;">
                                    <strong>Passport Number</strong> 護照號碼 (If applicable)
                                </td>
                                <td class="label-cell" colspan="2" style="width: 50%;">
                                    <strong>Native Language</strong> 母語
                                </td>
                            </tr>
                            <tr>
                                <td class="value-cell" colspan="2" style="width: 50%;">
                                    No. {{ applicationData.passportNumber || '' }}
                                </td>
                                <td class="value-cell" colspan="2" style="width: 50%;">
                                    {{ applicationData.nativeLanguage || '' }}
                                </td>
                            </tr>

                            <tr>
                                <td class="label-cell" colspan="2" style="width: 50%;">
                                    <strong>Gender</strong> 性別：M(男)
                                </td>
                                <td class="label-cell" colspan="2" style="width: 50%;">
                                    <strong>Marital Status</strong> 婚姻狀況：Married (已婚)
                                </td>
                            </tr>
                            <tr>
                                <td class="value-cell" colspan="2" style="width: 50%;">
                                    {{ getGenderText(applicationData.gender) }}
                                </td>
                                <td class="value-cell" colspan="2" style="width: 50%;">
                                    {{ getMaritalStatusText(applicationData.maritalStatus) }}
                                </td>
                            </tr>

                            <tr>
                                <td class="label-cell address-label" colspan="4">
                                    <strong>Permanent Address</strong> 戶籍地址
                                </td>
                            </tr>
                            <tr>
                                <td class="value-cell" colspan="4">
                                    Street 街道： {{ applicationData.permStreet || '' }}<br>
                                    City 城市： {{ applicationData.permCity || '' }}；{{
                                        getCountryName(applicationData.permCountry) || '' }}<br>
                                    Country 國家： {{ getCountryName(applicationData.permCountry) || '' }}<br>
                                    Zip code 郵遞區號： {{ applicationData.permZipCode || ''
                                    }}&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                                    Phone 電話： ++{{ applicationData.permPhoneCountry }}-{{ applicationData.permPhoneArea
                                    }}-{{ applicationData.permPhoneNumber }}
                                </td>
                            </tr>
                            <tr>
                                <td class="label-cell address-label" colspan="4">
                                    <strong>Mailing Address</strong> 郵寄地址
                                </td>
                            </tr>
                            <tr>
                                <td class="value-cell" colspan="4">
                                    Street 街道： {{ applicationData.mailStreet || '' }}<br>
                                    City 城市： {{ applicationData.mailCity || '' }}；{{
                                        getCountryName(applicationData.mailCountry) || '' }}<br>
                                    Country 國家： {{ getCountryName(applicationData.mailCountry) || '' }}<br>
                                    Zip code 郵遞區號： {{ applicationData.mailZipCode || ''
                                    }}&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                                    Phone 電話： ++{{ applicationData.mailPhoneCountry }}-{{ applicationData.mailPhoneArea
                                    }}-{{ applicationData.mailPhoneNumber }}
                                </td>
                            </tr>

                            <tr>
                                <td class="label-cell" colspan="4">
                                    <strong>Financial Support</strong> 學費來源
                                </td>
                            </tr>
                            <tr>
                                <td class="value-cell" colspan="4">
                                    {{ getFinancialSupportText() }}
                                </td>
                            </tr>

                        </table>
                    </div>

                    <!-- 頁尾 -->
                    <div class="page-footer">
                        <p class="page-number">Page 1</p>
                    </div>
                </div>

                <!-- 第二頁：申請資訊 -->
                <div class="print-page page-2">
                    <!-- 簡化的表頭 -->
                    <div class="form-header-simple">
                        <h2 class="university-name-zh">國立清華大學{{ applicationData.semesterInfo_Print }}外籍生申請表</h2>
                        <p class="applicant-info">Applicant No. 申請編號: {{ applicationData.applyID || '' }}</p>
                    </div>

                    <!-- 申請資訊區塊 -->
                    <div class="section application-info">
                        <h4 class="section-title">Application Information 申請資訊</h4>
                        
                        <!-- 如果有多筆學程,使用 v-for 循環顯示 -->
                        <div v-for="(program, index) in userPrograms" :key="program.pId" class="program-item">
                            <h5 class="program-number">Program Choice {{ index + 1 }} 志願 {{ index + 1 }}</h5>
                            <table class="info-table">
                                <tr>
                                    <td class="label-cell" style="width: 25%;">
                                        <strong>Degree Seeking</strong> 學位：
                                    </td>
                                    <td class="value-cell" colspan="3" style="width: 75%;">
                                        {{ getDegreeTextByProgram(program) }}
                                    </td>
                                </tr>
                                <tr>
                                    <td class="label-cell" style="width: 25%;">
                                        <strong>College</strong> 學院：
                                    </td>
                                    <td class="value-cell" colspan="3" style="width: 75%;">
                                        {{ program.college }}
                                    </td>
                                </tr>
                                <tr>
                                    <td class="label-cell" style="width: 25%;">
                                        <strong>Program Desired</strong> 系所：
                                    </td>
                                    <td class="value-cell" colspan="3" style="width: 75%;">
                                        {{ program.program }}
                                    </td>
                                </tr>
                            </table>
                        </div>

                        <!-- 如果沒有學程資料,顯示空白 -->
                        <div v-if="!userPrograms || userPrograms.length === 0" class="program-item">
                            <table class="info-table">
                                <tr>
                                    <td class="label-cell" style="width: 25%;">
                                        <strong>Degree Seeking</strong> 學位：
                                    </td>
                                    <td class="value-cell" colspan="3" style="width: 75%;">
                                        
                                    </td>
                                </tr>
                                <tr>
                                    <td class="label-cell" style="width: 25%;">
                                        <strong>College</strong> 學院：
                                    </td>
                                    <td class="value-cell" colspan="3" style="width: 75%;">
                                        
                                    </td>
                                </tr>
                                <tr>
                                    <td class="label-cell" style="width: 25%;">
                                        <strong>Program Desired</strong> 系所：
                                    </td>
                                    <td class="value-cell" colspan="3" style="width: 75%;">
                                        
                                    </td>
                                </tr>
                            </table>
                        </div>
                    </div>

                    <!-- 頁尾 -->
                    <div class="page-footer">
                        <p class="page-number">Page 2</p>
                    </div>
                </div>

                <!-- 第三頁：教育背景資訊 -->
                <div class="print-page page-3">
                    <!-- 簡化的表頭 -->
                    <div class="form-header-simple">
                        <h2 class="university-name-zh">國立清華大學{{ applicationData.semesterInfo_Print }}外籍生申請表</h2>
                        <p class="applicant-info">Applicant No. 申請編號: {{ applicationData.applyID || '' }}</p>
                    </div>

                    <!-- 教育背景資訊 -->
                    <div class="section">
                        <h4 class="section-title">Education Information 教育背景</h4>
                        
                        <!-- Secondary Schools 高中 -->
                        <template v-if="hasHighSchoolInfo()">
                            <div class="education-section">
                                <h5 class="education-level">Secondary Schools 高中</h5>
                                <table class="info-table">
                                    <tr>
                                        <td class="label-cell" style="width: 25%;">
                                            <strong>Name of School</strong> 校名:
                                        </td>
                                        <td class="value-cell" style="width: 75%;">
                                            {{ applicationData.highSchoolName || '' }}
                                        </td>
                                    </tr>
                                    <tr>
                                        <td class="label-cell" style="width: 25%;">
                                            <strong>Dates Attended</strong> 入學時間:
                                        </td>
                                        <td class="value-cell" style="width: 75%;">
                                            {{ applicationData.highSchoolAttended || '' }}
                                        </td>
                                    </tr>
                                    <tr>
                                        <td class="label-cell" style="width: 25%;">
                                            <strong>Date of Graduation</strong> 畢業時間:
                                        </td>
                                        <td class="value-cell" style="width: 75%;">
                                            {{ applicationData.highSchoolGraduation || '' }}
                                        </td>
                                    </tr>
                                    <tr>
                                        <td class="label-cell" colspan="2">
                                            <strong>Address of School</strong> 學校地址
                                        </td>
                                    </tr>
                                    <tr>
                                        <td class="value-cell" colspan="2">
                                            <strong>Street</strong> 街道: {{ applicationData.highSchoolStreet || '' }}<br>
                                            <strong>City</strong> 城市: {{ applicationData.highSchoolCity || '' }}<br>
                                            <strong>Country</strong> 國家: {{ getCountryName(applicationData.highSchoolCountry) || applicationData.highSchoolCountry || '' }}
                                        </td>
                                    </tr>
                                </table>
                            </div>
                        </template>

                        <!-- Colleges/Universities 大學 -->
                        <template v-if="hasUndergraduateInfo()">
                            <div class="education-section">
                                <h5 class="education-level">Colleges/universities 大學</h5>
                                <table class="info-table">
                                    <tr>
                                        <td class="label-cell" style="width: 25%;">
                                            <strong>Name of School</strong> 校名:
                                        </td>
                                        <td class="value-cell" style="width: 75%;">
                                            {{ applicationData.undergraduateName || '' }}
                                        </td>
                                    </tr>
                                    <tr>
                                        <td class="label-cell" style="width: 25%;">
                                            <strong>Degree Received</strong> 獲得學位:
                                        </td>
                                        <td class="value-cell" style="width: 75%;">
                                            {{ applicationData.undergraduateReceive || '' }}
                                        </td>
                                    </tr>
                                    <tr>
                                        <td class="label-cell" style="width: 25%;">
                                            <strong>Dates Attended</strong> 入學時間:
                                        </td>
                                        <td class="value-cell" style="width: 75%;">
                                            {{ applicationData.undergraduateAttended || '' }}
                                        </td>
                                    </tr>
                                    <tr>
                                        <td class="label-cell" style="width: 25%;">
                                            <strong>Date of Graduation</strong> 畢業時間:
                                        </td>
                                        <td class="value-cell" style="width: 75%;">
                                            {{ applicationData.undergraduateGraduation || '' }}
                                        </td>
                                    </tr>
                                    <tr>
                                        <td class="label-cell" colspan="2">
                                            <strong>Address of School</strong> 學校地址
                                        </td>
                                    </tr>
                                    <tr>
                                        <td class="value-cell" colspan="2">
                                            <strong>Street</strong> 街道: {{ applicationData.undergraduateStreet || '' }}<br>
                                            <strong>City</strong> 城市: {{ applicationData.undergraduateCity || '' }}<br>
                                            <strong>Country</strong> 國家: {{ getCountryName(applicationData.undergraduateCountry) || applicationData.undergraduateCountry || '' }}
                                        </td>
                                    </tr>
                                </table>
                            </div>
                        </template>

                        <!-- Graduate School 研究所 -->
                        <template v-if="hasGraduateInfo()">
                            <div class="education-section">
                                <h5 class="education-level">Graduate School 研究所</h5>
                                <table class="info-table">
                                    <tr>
                                        <td class="label-cell" style="width: 25%;">
                                            <strong>Name of School</strong> 校名:
                                        </td>
                                        <td class="value-cell" style="width: 75%;">
                                            {{ applicationData.graduateName || '' }}
                                        </td>
                                    </tr>
                                    <tr>
                                        <td class="label-cell" style="width: 25%;">
                                            <strong>Degree Received</strong> 獲得學位:
                                        </td>
                                        <td class="value-cell" style="width: 75%;">
                                            {{ applicationData.graduateReceive || '' }}
                                        </td>
                                    </tr>
                                    <tr>
                                        <td class="label-cell" style="width: 25%;">
                                            <strong>Dates Attended</strong> 入學時間:
                                        </td>
                                        <td class="value-cell" style="width: 75%;">
                                            {{ applicationData.graduateAttended || '' }}
                                        </td>
                                    </tr>
                                    <tr>
                                        <td class="label-cell" style="width: 25%;">
                                            <strong>Date of Graduation</strong> 畢業時間:
                                        </td>
                                        <td class="value-cell" style="width: 75%;">
                                            {{ applicationData.graduateGraduation || '' }}
                                        </td>
                                    </tr>
                                    <tr>
                                        <td class="label-cell" colspan="2">
                                            <strong>Address of School</strong> 學校地址
                                        </td>
                                    </tr>
                                    <tr>
                                        <td class="value-cell" colspan="2">
                                            <strong>Street</strong> 街道: {{ applicationData.graduateStreet || '' }}<br>
                                            <strong>City</strong> 城市: {{ applicationData.graduateCity || '' }}<br>
                                            <strong>Country</strong> 國家: {{ getCountryName(applicationData.graduateCountry) || applicationData.graduateCountry || '' }}
                                        </td>
                                    </tr>
                                </table>
                            </div>
                        </template>

                        <!-- Have you previously studied in Taiwan? -->
                        <div class="education-section">
                            <table class="info-table">
                                <tr>
                                    <td class="label-cell" colspan="2" style="width: 50%;">
                                        <strong>Have you previously studied in Taiwan?</strong> 是否曾在臺灣就學?
                                    </td>                                    
                                    <td class="value-cell" colspan="2" style="width: 50%;">
                                        {{ getYesNoText(applicationData.studiedInTaiwan) }}
                                    </td>
                                </tr>
                            </table>
                        </div>
                    </div>

                    <!-- 頁尾 -->
                    <div class="page-footer">
                        <p class="page-number">Page 3</p>
                    </div>
                </div>

                <!-- 第四頁：推薦人資訊 -->
                <div class="print-page page-4">
                    <!-- 簡化的表頭 -->
                    <div class="form-header-simple">
                        <h2 class="university-name-zh">國立清華大學{{ applicationData.semesterInfo_Print }}外籍生申請表</h2>
                        <p class="applicant-info">Applicant No. 申請編號: {{ applicationData.applyID || '' }}</p>
                    </div>

                    <!-- 推薦人資訊 -->
                    <div class="section">
                        <h4 class="section-title">Academic Reference 推薦人資訊</h4>
                        
                        <!-- 如果有推薦人資料,使用 v-for 循環顯示 -->
                        <template v-if="recommenders && recommenders.length > 0">
                            <div v-for="(rec, index) in recommenders" :key="rec.arId" class="reference-section">
                                <h5 class="reference-number">Academic Reference 推薦人資訊 {{ index + 1 }}</h5>
                                <table class="info-table">
                                    <tr>
                                        <td class="label-cell" style="width: 25%;">
                                            <strong>Name</strong> 姓名:
                                        </td>
                                        <td class="value-cell" style="width: 25%;">
                                            {{ rec.name || '' }}
                                        </td>
                                        <td class="label-cell" style="width: 25%;">
                                            <strong>Title</strong> 職稱:
                                        </td>
                                        <td class="value-cell" style="width: 25%;">
                                            {{ rec.jobTitle || '' }}
                                        </td>
                                    </tr>
                                    <tr>
                                        <td class="label-cell" colspan="1" style="width: 25%;">
                                            <strong>School</strong> 學校/機構:
                                        </td>
                                        <td class="value-cell" colspan="3" style="width: 75%;">
                                            {{ rec.schoolOrInstitute || '' }}
                                        </td>
                                    </tr>
                                    <tr>
                                        <td class="label-cell" colspan="1" style="width: 25%;">
                                            <strong>Contact Email</strong> 電子信箱:
                                        </td>
                                        <td class="value-cell" colspan="3" style="width: 75%;">
                                            {{ rec.email || '' }}
                                        </td>
                                    </tr>
                                </table>
                            </div>
                        </template>

                        <!-- 如果沒有推薦人資料,顯示空白表格 -->
                        <template v-else>
                            <div v-for="n in 3" :key="n" class="reference-section">
                                <h5 class="reference-number">Academic Reference 推薦人資訊 {{ n }}</h5>
                                <table class="info-table">
                                    <tr>
                                        <td class="label-cell" style="width: 30%;">
                                            <strong>Name</strong> 姓名:
                                        </td>
                                        <td class="value-cell" style="width: 20%;">
                                            
                                        </td>
                                        <td class="label-cell" style="width: 30%;">
                                            <strong>Title</strong> 職稱:
                                        </td>
                                        <td class="value-cell" style="width: 20%;">
                                            
                                        </td>
                                    </tr>
                                    <tr>
                                        <td class="label-cell" colspan="1">
                                            <strong>School</strong> 學校/機構:
                                        </td>
                                        <td class="value-cell" colspan="3">
                                            
                                        </td>
                                    </tr>
                                    <tr>
                                        <td class="label-cell" colspan="1">
                                            <strong>Contact Email</strong> 電子信箱:
                                        </td>
                                        <td class="value-cell" colspan="3">
                                            
                                        </td>
                                    </tr>
                                </table>
                            </div>
                        </template>
                    </div>

                    <!-- 頁尾 -->
                    <div class="page-footer">
                        <p class="page-number">Page 4</p>
                    </div>
                </div>

                <!-- 第五頁：語言能力 -->
                <div class="print-page page-5">
                    <!-- 簡化的表頭 -->
                    <div class="form-header-simple">
                        <h2 class="university-name-zh">國立清華大學{{ applicationData.semesterInfo_Print }}外籍生申請表</h2>
                        <p class="applicant-info">Applicant No. 申請編號: {{ applicationData.applyID || '' }}</p>
                    </div>

                    <!-- 語言能力資訊 -->
                    <div class="section">
                        <h4 class="section-title">Language Proficiency 語文能力</h4>
                        
                        <!-- English Proficiency 英文能力 -->
                        <div class="language-section">
                            <h5 class="language-title">English Proficiency 英文能力</h5>
                            <table class="info-table language-table">
                                <tr>
                                    <td class="language-option" :class="{ 'selected': applicationData.languageEnglish == 5 }">
                                        <span class="radio-circle" :class="{ 'filled': applicationData.languageEnglish == 5 }"></span>
                                        Fluent
                                    </td>
                                    <td class="language-option" :class="{ 'selected': applicationData.languageEnglish == 4 }">
                                        <span class="radio-circle" :class="{ 'filled': applicationData.languageEnglish == 4 }"></span>
                                        Good
                                    </td>
                                    <td class="language-option" :class="{ 'selected': applicationData.languageEnglish == 3 }">
                                        <span class="radio-circle" :class="{ 'filled': applicationData.languageEnglish == 3 }"></span>
                                        Moderate
                                    </td>
                                    <td class="language-option" :class="{ 'selected': applicationData.languageEnglish == 2 }">
                                        <span class="radio-circle" :class="{ 'filled': applicationData.languageEnglish == 2 }"></span>
                                        Limited
                                    </td>
                                    <td class="language-option" :class="{ 'selected': applicationData.languageEnglish == 1 }">
                                        <span class="radio-circle" :class="{ 'filled': applicationData.languageEnglish == 1 }"></span>
                                        None
                                    </td>
                                </tr>
                            </table>
                        </div>

                        <!-- Chinese Proficiency 中文能力 -->
                        <div class="language-section">
                            <h5 class="language-title">Chinese Proficiency 中文能力</h5>
                            <table class="info-table language-table">
                                <tr>
                                    <td class="language-option" :class="{ 'selected': applicationData.languageMandarin == 5 }">
                                        <span class="radio-circle" :class="{ 'filled': applicationData.languageMandarin == 5 }"></span>
                                        Fluent
                                    </td>
                                    <td class="language-option" :class="{ 'selected': applicationData.languageMandarin == 4 }">
                                        <span class="radio-circle" :class="{ 'filled': applicationData.languageMandarin == 4 }"></span>
                                        Good
                                    </td>
                                    <td class="language-option" :class="{ 'selected': applicationData.languageMandarin == 3 }">
                                        <span class="radio-circle" :class="{ 'filled': applicationData.languageMandarin == 3 }"></span>
                                        Moderate
                                    </td>
                                    <td class="language-option" :class="{ 'selected': applicationData.languageMandarin == 2 }">
                                        <span class="radio-circle" :class="{ 'filled': applicationData.languageMandarin == 2 }"></span>
                                        Limited
                                    </td>
                                    <td class="language-option" :class="{ 'selected': applicationData.languageMandarin == 1 }">
                                        <span class="radio-circle" :class="{ 'filled': applicationData.languageMandarin == 1 }"></span>
                                        None
                                    </td>
                                </tr>
                            </table>
                        </div>

                        <!-- Other Language 其他語言 -->
                        <div class="language-section" v-if="applicationData.languageOtherName">
                            <h5 class="language-title">Other {{ applicationData.languageOtherName || '' }}</h5>
                            <table class="info-table language-table">
                                <tr>
                                    <td class="language-option" :class="{ 'selected': applicationData.languageOtherLevel == 5 }">
                                        <span class="radio-circle" :class="{ 'filled': applicationData.languageOtherLevel == 5 }"></span>
                                        Fluent
                                    </td>
                                    <td class="language-option" :class="{ 'selected': applicationData.languageOtherLevel == 4 }">
                                        <span class="radio-circle" :class="{ 'filled': applicationData.languageOtherLevel == 4 }"></span>
                                        Good
                                    </td>
                                    <td class="language-option" :class="{ 'selected': applicationData.languageOtherLevel == 3 }">
                                        <span class="radio-circle" :class="{ 'filled': applicationData.languageOtherLevel == 3 }"></span>
                                        Moderate
                                    </td>
                                    <td class="language-option" :class="{ 'selected': applicationData.languageOtherLevel == 2 }">
                                        <span class="radio-circle" :class="{ 'filled': applicationData.languageOtherLevel == 2 }"></span>
                                        Limited
                                    </td>
                                    <td class="language-option" :class="{ 'selected': applicationData.languageOtherLevel == 1 }">
                                        <span class="radio-circle" :class="{ 'filled': applicationData.languageOtherLevel == 1 }"></span>
                                        None
                                    </td>
                                </tr>
                            </table>
                        </div>
                    </div>

                    <!-- 頁尾 -->
                    <div class="page-footer">
                        <p class="page-number">Page 5</p>
                    </div>
                </div>

                <!-- 第六頁：家庭資訊、緊急聯絡人和其他資訊 -->
                <div class="print-page page-6">
                    <!-- 簡化的表頭 -->
                    <div class="form-header-simple">
                        <h2 class="university-name-zh">國立清華大學{{ applicationData.semesterInfo_Print }}外籍生申請表</h2>
                        <p class="applicant-info">Applicant No. 申請編號: {{ applicationData.applyID || '' }}</p>
                    </div>

                    <!-- 家庭資訊 -->
                    <div class="section">
                        <h4 class="section-title">Information of Family 家庭資料</h4>
                        
                        <!-- Father's Information 父親資料 -->
                        <div class="family-section">
                            <h5 class="family-title">Father's Name 父親</h5>
                            <table class="info-table">
                                <tr>
                                    <td class="label-cell" style="width: 25%;">
                                        <strong>Name</strong> 姓名:
                                    </td>
                                    <td class="value-cell" style="width: 25%;">
                                        {{ applicationData.fatherName || '' }}
                                    </td>
                                    <td class="label-cell" style="width: 25%;">
                                        <strong>Phone</strong> 電話:
                                    </td>
                                    <td class="value-cell" style="width: 25%;">
                                        ++{{ applicationData.fatherPhoneCountry }}-{{ applicationData.fatherPhoneArea }}-{{ applicationData.fatherPhoneNumber }}
                                    </td>
                                </tr>
                                <tr>
                                    <td class="label-cell" style="width: 25%;">
                                        <strong>Country of Birth</strong> 出生地:
                                    </td>
                                    <td class="value-cell" colspan="3" style="width: 75%;">
                                        {{ getCountryName(applicationData.fatherCountryOfBirth) || applicationData.fatherCountryOfBirth || '' }}
                                    </td>
                                </tr>
                                <tr>
                                    <td class="label-cell" style="width: 25%;">
                                        <strong>Country of Citizenship</strong> 國籍:
                                    </td>
                                    <td class="value-cell" colspan="3" style="width: 75%;">
                                        {{ getCountryName(applicationData.fatherCountryOfCitizenship) || applicationData.fatherCountryOfCitizenship || '' }}
                                    </td>
                                </tr>
                            </table>
                        </div>

                        <!-- Mother's Information 母親資料 -->
                        <div class="family-section">
                            <h5 class="family-title">Mother's Name 母親</h5>
                            <table class="info-table">
                                <tr>
                                    <td class="label-cell" style="width: 25%;">
                                        <strong>Name</strong> 姓名:
                                    </td>
                                    <td class="value-cell" style="width: 25%;">
                                        {{ applicationData.motherName || '' }}
                                    </td>
                                    <td class="label-cell" style="width: 25%;">
                                        <strong>Phone</strong> 電話:
                                    </td>
                                    <td class="value-cell" style="width: 25%;">
                                        ++{{ applicationData.motherPhoneCountry }}-{{ applicationData.motherPhoneArea }}-{{ applicationData.motherPhoneNumber }}
                                    </td>
                                </tr>
                                <tr>
                                    <td class="label-cell" style="width: 25%;">
                                        <strong>Country of Birth</strong> 出生地:
                                    </td>
                                    <td class="value-cell" colspan="3" style="width: 75%;">
                                        {{ getCountryName(applicationData.motherCountryOfBirth) || applicationData.motherCountryOfBirth || '' }}
                                    </td>
                                </tr>
                                <tr>
                                    <td class="label-cell" style="width: 25%;">
                                        <strong>Country of Citizenship</strong> 國籍:
                                    </td>
                                    <td class="value-cell" colspan="3" style="width: 75%;">
                                        {{ getCountryName(applicationData.motherCountryOfCitizenship) || applicationData.motherCountryOfCitizenship || '' }}
                                    </td>
                                </tr>
                            </table>
                        </div>
                    </div>

                    <!-- 緊急聯絡人 -->
                    <div class="section">
                        <h4 class="section-title">Contact Person for Emergency 緊急聯絡人</h4>
                        <table class="info-table">
                            <tr>
                                <td class="label-cell" style="width: 25%;">
                                    <strong>Name</strong> 姓名:
                                </td>
                                <td class="value-cell" style="width: 25%;">
                                    {{ applicationData.emergencyName || '' }}
                                </td>
                                <td class="label-cell" style="width: 25%;">
                                    <strong>Relationship</strong> 關係:
                                </td>
                                <td class="value-cell" style="width: 25%;">
                                    {{ applicationData.emergencyRelationship || '' }}
                                </td>
                            </tr>
                            <tr>
                                <td class="label-cell" style="width: 25%;">
                                    <strong>Phone</strong> 電話:
                                </td>
                                <td class="value-cell" colspan="3" style="width: 75%;">
                                    ++{{ applicationData.emergencyPhoneCountry }}-{{ applicationData.emergencyPhoneArea }}-{{ applicationData.emergencyPhoneNumber }}
                                </td>
                            </tr>
                            <tr>
                                <td class="label-cell" style="width: 25%;">
                                    <strong>Email</strong> 電子信箱:
                                </td>
                                <td class="value-cell" colspan="3" style="width: 75%;">
                                    {{ applicationData.emergencyEmail || '' }}
                                </td>
                            </tr>
                            <tr>
                                <td class="label-cell" style="width: 25%;">
                                    <strong>Street</strong> 街道:
                                </td>
                                <td class="value-cell" colspan="3" style="width: 75%;">
                                    {{ applicationData.emergencyStreet || '' }}
                                </td>
                            </tr>
                            <tr>
                                <td class="label-cell" style="width: 25%;">
                                    <strong>City</strong> 城市:
                                </td>
                                <td class="value-cell" colspan="3" style="width: 75%;">
                                    {{ applicationData.emergencyCity || '' }}
                                </td>
                            </tr>
                            <tr>
                                <td class="label-cell" style="width: 25%;">
                                    <strong>Country</strong> 國家:
                                </td>
                                <td class="value-cell" colspan="3" style="width: 75%;">
                                    {{ getCountryName(applicationData.emergencyCountry) || applicationData.emergencyCountry || '' }}
                                </td>
                            </tr>
                        </table>
                    </div>

                    <!-- 其他資訊 -->
                    <div class="section">
                        <h4 class="section-title">Others 其他資訊</h4>
                        <table class="info-table">
                            <tr>
                                <td class="label-cell" colspan="4">
                                    <strong>Do you have Alien Resident Certificate(ARC)?</strong>
                                </td>
                            </tr>
                            <tr>
                                <td class="value-cell" colspan="4">
                                    {{ getYesNoText(applicationData.hasTaiwanARC) }}<span v-if="applicationData.hasTaiwanAPRC == 1">, ARC Number(若有請填寫): {{ applicationData.taiwanARCNumber || '' }}</span>
                                </td>
                            </tr>
                            <tr>
                                <td class="label-cell" colspan="4">
                                    <strong>Do you have Taiwan APRC (Alien Permanent Resident Certificate)</strong> 是否有永久居留證? 
                                </td>
                            </tr>
                            <tr>
                                <td class="value-cell" colspan="4">
                                    {{ getYesNoText(applicationData.hasTaiwanAPRC) }}<span v-if="applicationData.hasTaiwanAPRC == 1">, APRC Number(若有請填寫): {{ applicationData.taiwanAPRCNumber || '' }}</span>
                                </td>
                            </tr>
                            <tr>
                                <td class="label-cell" colspan="4">
                                    <strong>How did you find out about National Tsing Hua University?</strong> 如何得知清華大學?
                                </td>
                            </tr>
                            <tr>
                                <td class="value-cell" colspan="4">
                                    {{ getMessageSourceText() }}
                                </td>
                            </tr>
                        </table>
                    </div>

                    <!-- 同意聲明 -->
                    <div class="section">
                        <table class="info-table">
                            <tr>
                                <td class="value-cell" colspan="4">
                                    <strong>I understand that if any of this information is found to be substantially inaccurate or incomplete,it may result in termination of my application or status as NTHU student.</strong><br>
                                    我已瞭解上述資料如未完整告知或與事實不符，經查證屬實者將取消我的申請資格。如已獲得入學許可亦將喪失學籍資格。
                                </td>
                            </tr>
                        </table>
                    </div>

                    <!-- 簽名欄 -->
                    <div class="signature-section">
                        <div class="signature-row">
                            <div class="signature-item">
                                <strong>Signature(簽名):</strong>
                                <div class="signature-line"></div>
                            </div>
                            <div class="signature-item">
                                <strong>Date 日期:</strong>
                                <div class="signature-line"></div>
                            </div>
                        </div>
                    </div>

                    <!-- 頁尾 -->
                    <div class="page-footer">
                        <p class="page-number">Page 6</p>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import applyStudentService from "@/services/applyStudent.js";

export default {
    name: "ApplicationFormPrintPage",
    data() {
        return {
            isLoading: true,
            applicationData: null,
            userPrograms: [],
            countries: [],
            aId: null,
            recommenders: [],
            availableMessageSources: [],
        };
    },
    async mounted() {
        await this.loadApplicationData();
        // 資料載入完成後自動觸發列印對話框
        if (this.applicationData) {
            // 設定頁面標題
            document.title = `國立清華大學外籍生申請表-${this.applicationData.applyID || ''}`;
            
            setTimeout(() => {
                window.print();
            }, 500);
        }
    },
    methods: {
        async loadApplicationData() {
            try {
                // 載入申請表資料、國家列表、訊息來源選項
                const [result, countriesResult, messageSourcesResult] = await Promise.all([
                    applyStudentService.getUserApplication(),
                    applyStudentService.getAvailableCountries(),
                    applyStudentService.getAvailableMessageSources()
                ]);

                if (countriesResult.success && countriesResult.data) {
                    this.countries = countriesResult.data;
                }

                if (messageSourcesResult.success && messageSourcesResult.data) {
                    this.availableMessageSources = messageSourcesResult.data;
                }

                if (result.success && result.data && result.data.hasApplication) {
                    this.applicationData = result.data;
                    this.aId = result.data.aId;

                    // 載入學程資訊
                    if (this.applicationData.applyPrograms) {
                        try {
                            const programIds = JSON.parse(this.applicationData.applyPrograms);
                            await this.loadProgramsInfo(programIds);
                        } catch (error) {
                            console.error("Error parsing applyPrograms:", error);
                        }
                    }

                    // 載入推薦人資料
                    await this.loadRecommenders();
                }
            } catch (error) {
                console.error("Failed to load application data:", error);
                alert("Failed to load application data. Please close this window and try again.");
            } finally {
                this.isLoading = false;
            }
        },

        async loadProgramsInfo(programIds) {
            const programsResult = await applyStudentService.getActiveApplyPrograms(this.aId);
            if (programsResult.success && programsResult.data) {
                const { programs, colleges } = programsResult.data;
                const allPrograms = programs || [];

                this.userPrograms = [];
                for (const pId of programIds) {
                    const program = allPrograms.find(p => p.pId === pId);
                    if (program) {
                        const college = colleges?.find(c => c.cId === program.cId);

                        this.userPrograms.push({
                            pId: pId,
                            college: college?.name_EN || college?.name || 'Unknown College',
                            program: program.name_EN || program.name,
                            degree: program.degree,
                        });
                    }
                }
            }
        },

        getGenderText(gender) {
            if (gender === 1) return 'Male';
            if (gender === 2) return 'Female';
            return '';
        },

        getMaritalStatusText(status) {
            if (status === 1) return 'Single';
            if (status === 2) return 'Married';
            return '';
        },

        getCountryName(countryCode) {
            if (!countryCode || !this.countries) return countryCode || '';
            const country = this.countries.find(c => c.code === countryCode || c.name === countryCode);
            return country ? (country.name_EN || country.name) : countryCode;
        },

        getDegreeText() {
            if (!this.userPrograms || this.userPrograms.length === 0) return '';
            const program = this.userPrograms[0];
            if (program.degree === 8) return 'Bachelor Degree';
            if (program.degree === 10) return 'Master Degree';
            if (program.degree === 12) return 'Doctoral';
            return '';
        },

        getDegreeTextByProgram(program) {
            if (!program) return '';
            if (program.degree === 8) return 'Bachelor Degree';
            if (program.degree === 10) return 'Master Degree';
            if (program.degree === 12) return 'Doctoral';
            return '';
        },

        getCollegeText() {
            if (!this.userPrograms || this.userPrograms.length === 0) return '';
            return this.userPrograms[0].college || '';
        },

        getProgramText() {
            if (!this.userPrograms || this.userPrograms.length === 0) return '';
            return this.userPrograms[0].program || '';
        },

        getFinancialSupportText() {
            if (!this.applicationData.financial) {
                return 'Self / Scholarship(Please indicate the named scholarship that you received or will apply): NTHU INTERNATIONAL STUDENT SCHOLARSHIP';
            }

            try {
                // 如果 financial 是字串，先解析成陣列
                let financialData = this.applicationData.financial;
                if (typeof financialData === 'string') {
                    financialData = JSON.parse(financialData);
                }

                // 如果是空陣列
                if (!financialData || financialData.length === 0) {
                    return 'Self / Scholarship(Please indicate the named scholarship that you received or will apply): NTHU INTERNATIONAL STUDENT SCHOLARSHIP';
                }

                // 根據實際的財務支援資料生成文字
                return financialData.map(f => {
                    let text = f.name || 'Self';
                    if (f.scholarshipText) {
                        text += `: ${f.scholarshipText}`;
                    }
                    if (f.otherText) {
                        text += `: ${f.otherText}`;
                    }
                    return text;
                }).join(', ');
            } catch (error) {
                console.error('Error parsing financial data:', error);
                return 'Self / Scholarship(Please indicate the named scholarship that you received or will apply): NTHU INTERNATIONAL STUDENT SCHOLARSHIP';
            }
        },

        getScholarshipApplicationText() {
            if (this.applicationData.scholarshipApplication === 1) {
                return 'Apply for NTHU International Student Scholarship';
            }
            return '';
        },

        getPhotoUrl() {
            const photoPath = this.applicationData?.photoPreviewUrl;
            if (!photoPath) return '';
            // 如果已經是完整 URL，直接返回
            if (photoPath.startsWith('http')) return photoPath;
            // 否則組合完整路徑
            return this.$store?.state?.src + this.$store?.state?.subDirectory + photoPath || photoPath;
        },

        hasHighSchoolInfo() {
            return this.applicationData.highSchoolName ||
                this.applicationData.highSchoolCity ||
                this.applicationData.highSchoolCountry;
        },

        hasUndergraduateInfo() {
            return this.applicationData.undergraduateName ||
                this.applicationData.undergraduateCity ||
                this.applicationData.undergraduateCountry;
        },

        hasGraduateInfo() {
            return this.applicationData.graduateName ||
                this.applicationData.graduateCity ||
                this.applicationData.graduateCountry;
        },

        getYesNoText(value) {
            if (value === 1) return 'YES(是)';
            if (value === 0 || value === 2) return 'NO(否)';
            return '';
        },

        async loadRecommenders() {
            try {
                const result = await applyStudentService.getRecommenders(this.applicationData.asId);
                if (result.success && result.data) {
                    this.recommenders = result.data;
                }
            } catch (error) {
                console.error('Failed to load recommenders:', error);
            }
        },

        getMessageSourceText() {
            if (!this.applicationData.messageSource) {
                return 'N/A';
            }

            try {
                let messageSourceData = this.applicationData.messageSource;
                if (typeof messageSourceData === 'string') {
                    messageSourceData = JSON.parse(messageSourceData);
                }

                if (!messageSourceData || messageSourceData.length === 0) {
                    return 'N/A';
                }

                return messageSourceData.map(source => {
                    // 根據 msId 查找名稱
                    const sourceItem = this.availableMessageSources.find(
                        ms => ms.msId === source.msId
                    );
                    let text = sourceItem 
                        ? (sourceItem.name_EN || sourceItem.name) 
                        : `Message Source ID: ${source.msId}`;

                    if (source.otherText) {
                        text += ` (${source.otherText})`;
                    }
                    return text;
                }).join(', ');
            } catch (error) {
                console.error('Error parsing message source data:', error);
                return this.applicationData.messageSource;
            }
        }
    },
};
</script>

<style scoped>
.print-page-container {
    min-height: 100vh;
    background: white;
}

.loading-container {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 100vh;
    font-size: 20px;
}

.spinner {
    animation: spin 1s linear infinite;
}

@keyframes spin {
    0% {
        transform: rotate(0deg);
    }

    100% {
        transform: rotate(360deg);
    }
}

.print-content {
    width: 100%;
    background: white;
}

/* 列印表單樣式 */
.print-form-container {
    font-family: 'Times New Roman', Times, serif, '微軟正黑體', 'Microsoft JhengHei';
    background: white;
    color: black;
}

.print-page {
    width: 210mm;
    max-height: 297mm;
    padding: 15mm;
    margin: 0 auto;
    background: white;
    box-sizing: border-box;
    position: relative;
    overflow: hidden;
}

/* 表頭樣式 */
.form-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 15px;
    border-bottom: 2px solid #000;
    padding-bottom: 10px;
}

.header-left {
    flex: 1;
}

.university-name-zh {
    font-size: 18px;
    font-weight: bold;
    margin: 0 0 5px 0;
}

.university-name-en {
    font-size: 16px;
    font-weight: bold;
    margin: 0 0 3px 0;
}

.form-title {
    font-size: 14px;
    margin: 0;
}

.header-right {
    margin-left: 20px;
}

.photo-box {
    width: 35mm;
    height: 45mm;
    border: 2px solid #000;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #f5f5f5;
}

.photo-box img {
    max-width: 100%;
    max-height: 100%;
    object-fit: cover;
}

.photo-placeholder {
    color: #999;
    font-size: 14px;
}

/* 區塊樣式 */
.section {
    margin-bottom: 15px;
}

.section-title {
    background-color: #580d63;
    color: white;
    padding: 5px 10px;
    font-size: 14px;
    margin: 0 0 5px 0;
    font-weight: bold;
}

/* 表格樣式 */
.info-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 12px;
    line-height: 1.5;
}

.info-table td {
    border: 1px solid #000;
    padding: 4px 6px;
    vertical-align: top;
}

.label-cell {
    background-color: #f0f0f0;
    font-size: 11px;
    /* width: 25%; */
}

.value-cell {
    min-height: 20px;
    font-size: 12px;
}

.name-cell {
    /* width: 33.33%; */
    text-align: center;
    align-content: center;
}

.field-label {
    font-size: 9px;
    color: #666;
    font-style: italic;
}

.address-label {
    font-weight: bold;
}

/* 簡化的表頭樣式 (用於第2頁之後) */
.form-header-simple {
    border-bottom: 2px solid #000;
    padding-bottom: 10px;
    margin-bottom: 15px;
}

.form-header-simple .university-name-zh {
    font-size: 16px;
    font-weight: bold;
    margin: 0 0 5px 0;
}

.form-header-simple .applicant-info {
    font-size: 12px;
    margin: 0;
    color: #333;
}

/* 申請資訊區塊 */
.application-info {
    margin-top: 20px;
}

.program-item {
    margin-bottom: 20px;
}

.program-number {
    background-color: #7d1f86;
    color: white;
    padding: 4px 10px;
    font-size: 13px;
    margin: 0 0 5px 0;
    font-weight: bold;
}

/* 教育背景區塊 */
.education-section {
    margin-bottom: 15px;
}

.education-level {
    background-color: #f0f0f0;
    padding: 4px 10px;
    font-size: 12px;
    margin: 0 0 5px 0;
    font-weight: bold;
    border-left: 4px solid #580d63;
}

/* 推薦人區塊 */
.reference-section {
    margin-bottom: 15px;
}

.reference-number {
    background-color: #f0f0f0;
    padding: 4px 10px;
    font-size: 12px;
    margin: 0 0 5px 0;
    font-weight: bold;
    border-left: 4px solid #580d63;
}

/* 語言能力區塊 */
.language-section {
    margin-bottom: 20px;
}

.language-title {
    background-color: #f0f0f0;
    padding: 6px 10px;
    font-size: 12px;
    margin: 0 0 5px 0;
    font-weight: bold;
    border-left: 4px solid #580d63;
}

.language-table {
    border: 1px solid #000;
}

.language-option {
    text-align: center;
    padding: 8px 6px;
    font-size: 11px;
    width: 20%;
    background-color: white;
    position: relative;
}

.language-option.selected {
    background-color: #f0f0f0;
    font-weight: bold;
}

.radio-circle {
    display: inline-block;
    width: 12px;
    height: 12px;
    border: 2px solid #333;
    border-radius: 50%;
    margin-right: 4px;
    vertical-align: middle;
    position: relative;
}

.radio-circle.filled::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 6px;
    height: 6px;
    background-color: #333;
    border-radius: 50%;
}

/* 家庭資訊區塊 */
.family-section {
    margin-bottom: 15px;
}

.family-title {
    background-color: #f0f0f0;
    padding: 4px 10px;
    font-size: 12px;
    margin: 0 0 5px 0;
    font-weight: bold;
    border-left: 4px solid #580d63;
}

/* 簽名區塊 */
.signature-section {
    margin-top: 30px;
    margin-bottom: 20px;
}

.signature-row {
    display: flex;
    justify-content: space-between;
    gap: 40px;
}

.signature-item {
    flex: 1;
}

.signature-item strong {
    font-size: 12px;
    display: block;
    margin-bottom: 5px;
}

.signature-line {
    border-bottom: 1px solid #000;
    height: 30px;
    margin-top: 5px;
}

/* 頁尾 */
.page-footer {
    position: absolute;
    bottom: 10mm;
    left: 0;
    right: 0;
    text-align: center;
}

.page-number {
    font-size: 10px;
    color: #666;
}

/* 螢幕顯示時的樣式 */
@media screen {
    .loading-container {
        background: #f5f5f5;
    }

    .print-form-container {
        padding: 20px;
        background: #f5f5f5;
    }

    .print-page {
        box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
        margin-bottom: 20px;
    }
}

/* 列印樣式 */
@media print {
    .loading-container {
        display: none !important;
    }

    .print-page-container {
        margin: 0;
        padding: 0;
    }

    /* 確保標題在列印時顏色正常 */
    .section-title {
        background-color: #580d63 !important;
        color: white !important;
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
    }

    .program-number {
        background-color: #7d1f86 !important;
        color: white !important;
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
    }

    .education-level,
    .reference-number,
    .language-title,
    .family-title {
        background-color: #f0f0f0 !important;
        border-left: 4px solid #580d63 !important;
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
    }

    /* 確保語言能力選取項目在列印時清晰可見 */
    .language-option {
        border: 1px solid #000 !important;
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
    }

    .language-option.selected {
        background-color: #d0d0d0 !important;
        font-weight: bold !important;
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
    }

    .radio-circle {
        border: 2px solid #000 !important;
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
    }

    .radio-circle.filled::after {
        background-color: #000 !important;
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
    }

    .print-content {
        width: 100%;
        margin: 0;
        padding: 0;
    }

    .print-form-container {
        width: 100%;
        margin: 0;
        padding: 0;
        background: white;
        overflow: hidden;
    }

    .print-page {
        width: 210mm;
        height: 297mm;
        max-height: 297mm;
        margin: 0;
        padding: 15mm;
        page-break-after: always;
        page-break-before: auto;
        page-break-inside: avoid;
        overflow: hidden;
        box-sizing: border-box;
    }

    /* 最後一頁不需要分頁 */
    .print-page:last-child {
        page-break-after: auto;
    }

    /* A4 紙張設定 */
    @page {
        margin: 0;
        size: A4 portrait;
    }
}
</style>
